import React from 'react';
import { useAppSelector } from 'hooks';

import { Button } from '~components';
import { AuthTags } from '~constants/authTags';
import { selectUserTags } from '~main/selectors/login';

import {
  GocardlessMandate,
  NON_LINKABLE_MANDATE_TAGS,
} from '~direct-debits/models';

interface ComponentProps {
  row: GocardlessMandate;
  onLink?: (mandate: GocardlessMandate) => void;
  onIgnore?: (mandate: GocardlessMandate) => void;
  onRestore?: (mandate: GocardlessMandate) => void;
  onUnlink?: (mandate: GocardlessMandate) => void;
  isBusy?: boolean;
  isConnected?: boolean;
}

const DISCONNECTED_TITLE = 'GoCardless connection is not active';

const MandateActions: React.FC<ComponentProps> = ({
  row,
  onLink,
  onIgnore,
  onRestore,
  onUnlink,
  isBusy = false,
  isConnected = true,
}) => {
  const userTags = useAppSelector(selectUserTags);
  const canUnlock = userTags.includes(AuthTags.DIRECTDEBITS.UNLOCK);
  const isNonLinkable = NON_LINKABLE_MANDATE_TAGS.has(row.status.tag);

  if (row.is_ignored) {
    return (
      <Button
        id={`dd-mandate-restore-${row.id}`}
        data-cy={`dd-mandate-restore-${row.id}`}
        size="sm"
        color="primary"
        outline
        disabled={isBusy}
        onClick={() => onRestore?.(row)}
      >
        Restore
      </Button>
    );
  }

  if (row.is_linked) {
    if (!canUnlock) {
      return null;
    }
    return (
      <Button
        id={`dd-mandate-unlink-${row.id}`}
        data-cy={`dd-mandate-unlink-${row.id}`}
        size="sm"
        color="danger"
        outline
        disabled={isBusy || !isConnected}
        title={isConnected ? undefined : DISCONNECTED_TITLE}
        onClick={() => onUnlink?.(row)}
      >
        Unlink
      </Button>
    );
  }

  return (
    <div className="d-flex gap-2 justify-content-end">
      <Button
        id={`dd-mandate-link-${row.id}`}
        data-cy={`dd-mandate-link-${row.id}`}
        size="sm"
        color="primary"
        disabled={isBusy || isNonLinkable || !isConnected}
        title={isConnected ? undefined : DISCONNECTED_TITLE}
        onClick={() => onLink?.(row)}
      >
        Link
      </Button>
      <Button
        id={`dd-mandate-ignore-${row.id}`}
        data-cy={`dd-mandate-ignore-${row.id}`}
        size="sm"
        color="secondary"
        outline
        disabled={isBusy || !isConnected}
        title={isConnected ? undefined : DISCONNECTED_TITLE}
        onClick={() => onIgnore?.(row)}
      >
        Ignore
      </Button>
    </div>
  );
};

export default MandateActions;
